import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { PricingPlan, VIEW_MODE } from '../../../consts/enums';
import STRING_KEYS from '../../../language/keys';
import { style } from '../AccountInformationSection/style';

type AccountTrialOutreachToggleProps = {
  value: boolean;
  persistedValue: boolean;
  pricingPlan: PricingPlan | string | undefined;
  onValuesChange: (name: string, value: string | boolean) => void;
  viewMode: ViewMode;
};

/**
 * One-way toggle for `outreachEnabledForTrial`.
 *
 * Lock is based on the persisted (server-loaded) value, not the live Formik
 * value, so an admin can revert their unsaved local flip before submitting.
 * Once persisted as true, the toggle is permanently locked.
 */
export const AccountTrialOutreachToggle = ({
  value,
  persistedValue,
  pricingPlan,
  onValuesChange,
  viewMode,
}: AccountTrialOutreachToggleProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const isTrial = pricingPlan === PricingPlan.Trial;
  const isReadOnly = viewMode === VIEW_MODE.READ_ONLY;
  const isLocked = persistedValue === true;

  const disabled = isReadOnly || isLocked || !isTrial;
  const checked = !!value;

  let helperText: string | undefined;
  if (isLocked) {
    helperText = translate(STRING_KEYS.OUTREACH_TRIAL_OVERRIDE_HELPER_LOCKED);
  } else if (!isTrial) {
    helperText = translate(STRING_KEYS.OUTREACH_TRIAL_OVERRIDE_HELPER_DISABLED);
  }

  const handleSwitchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.checked;
      if (next === true && value === false) {
        setIsConfirmOpen(true);

        return;
      }
      // Reverting an unsaved local flip (true → false) is allowed; once
      // persistedValue is true the switch is `disabled` and this never fires.
      if (next === false && value === true) {
        onValuesChange('outreachEnabledForTrial', false);
      }
    },
    [value, onValuesChange],
  );

  const handleConfirm = useCallback(() => {
    setIsConfirmOpen(false);
    onValuesChange('outreachEnabledForTrial', true);
  }, [onValuesChange]);

  const handleCancel = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  return (
    <Box sx={ style.card.row.sx }>
      <Box>
        <FormControlLabel
          control={ (
            <Switch
              checked={ checked }
              disabled={ disabled }
              onChange={ handleSwitchChange }
              name="outreachEnabledForTrial"
              color="primary"
            />
          ) }
          label={ translate(STRING_KEYS.OUTREACH_TRIAL_OVERRIDE_LABEL) }
        />
        {helperText && (
          <FormHelperText>{ helperText }</FormHelperText>
        )}
      </Box>

      <Dialog
        open={ isConfirmOpen }
        onClose={ handleCancel }
        onClick={ (e) => e.stopPropagation() }
        fullWidth
      >
        <DialogTitle sx={ { mt: '15px' } }>
          <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
            <Typography variant="h5">
              { translate(STRING_KEYS.OUTREACH_TRIAL_OVERRIDE_CONFIRM_TITLE) }
            </Typography>
            <IconButton
              onClick={ handleCancel }
              sx={ { color: 'action.active' } }
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            { translate(STRING_KEYS.OUTREACH_TRIAL_OVERRIDE_CONFIRM_BODY) }
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={ { pr: '25px' } }>
          <Button
            onClick={ (e) => {
              e.stopPropagation();
              handleCancel();
            } }
          >
            { translate(STRING_KEYS.OUTREACH_TRIAL_OVERRIDE_CONFIRM_CANCEL) }
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={ (e) => {
              e.stopPropagation();
              handleConfirm();
            } }
          >
            { translate(STRING_KEYS.OUTREACH_TRIAL_OVERRIDE_CONFIRM_OK) }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountTrialOutreachToggle;
